import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { firstImageNode } from '@/core/document'
import { ReportingStage, surfaceError } from '@/core/reporting'
import {
  clearRecentEdits,
  deleteRecentEdit,
  InvalidRecentEditError,
  listRecentEdits,
  restoreRecentEdit,
  summarizeDocument,
  type RecentEditRecord,
} from '@/core/persistence'
import { useEditSession } from '@/core/store/editSession'
import { formatRelativeDate } from '@/lib/relativeDate'
import { TEST_IDS } from '@/lib/testIds'
import { Button } from '@/ui/shadcn/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/shadcn/dialog'

interface RecentEditsProps {
  hideWhenEmpty?: boolean
  onRestored?: () => void
}

function recordFileName(record: RecentEditRecord): string {
  const image = firstImageNode(record.document.root)
  const source = record.sources.find((candidate) => candidate.id === image.sourceId)
  if (!source) throw new Error(`Recent edit "${record.id}" is missing its root source`)
  return source.fileName
}

function Thumbnail({ record }: { record: RecentEditRecord }) {
  const source = useMemo(() => URL.createObjectURL(record.thumbnail), [record.thumbnail])
  useEffect(() => () => URL.revokeObjectURL(source), [source])
  return <img src={source} alt="" className="size-full object-cover" />
}

export function RecentEdits({ hideWhenEmpty = false, onRestored }: RecentEditsProps) {
  const currentDocument = useEditSession((state) => state.document)
  const persistedEditId = useEditSession((state) => state.persistedEditId)
  const [records, setRecords] = useState<readonly RecentEditRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingRestore, setPendingRestore] = useState<RecentEditRecord | null>(null)
  const [pendingDelete, setPendingDelete] = useState<RecentEditRecord | null>(null)
  const [confirmClear, setConfirmClear] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setRecords(await listRecentEdits())
    } catch (error) {
      surfaceError(error, 'Could not load recent edits.', { stage: ReportingStage.Persistence })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    let active = true
    void listRecentEdits()
      .then((nextRecords) => {
        if (active) setRecords(nextRecords)
      })
      .catch((error: unknown) => {
        if (!active) return
        if (error instanceof InvalidRecentEditError && error.recordId) {
          const recordId = error.recordId
          surfaceError(error, 'Could not load recent edits.', {
            stage: ReportingStage.Persistence,
            toastOptions: {
              action: {
                label: 'Delete invalid edit',
                onClick: () => {
                  void deleteRecentEdit(recordId)
                    .then(() => setRecords((current) => current.filter((record) => record.id !== recordId)))
                    .catch((deleteError: unknown) => {
                      surfaceError(deleteError, 'Could not delete the invalid edit.', { stage: ReportingStage.Persistence })
                    })
                },
              },
            },
          })
        } else {
          surfaceError(error, 'Could not load recent edits.', { stage: ReportingStage.Persistence })
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const restore = async (record: RecentEditRecord) => {
    try {
      await restoreRecentEdit(record.id)
      toast.success(`Restored ${recordFileName(record)}`)
      onRestored?.()
    } catch (error) {
      surfaceError(error, 'Could not restore the edit.', { stage: ReportingStage.Persistence })
    } finally {
      setPendingRestore(null)
    }
  }

  const requestRestore = (record: RecentEditRecord) => {
    if (currentDocument && persistedEditId !== record.id) setPendingRestore(record)
    else void restore(record)
  }

  const remove = async (record: RecentEditRecord) => {
    try {
      await deleteRecentEdit(record.id)
      if (persistedEditId === record.id) {
        useEditSession.getState().clearSource()
        onRestored?.()
      } else {
        await refresh()
      }
    } catch (error) {
      surfaceError(error, 'Could not delete the recent edit.', { stage: ReportingStage.Persistence })
    } finally {
      setPendingDelete(null)
    }
  }

  const clearAll = async () => {
    try {
      await clearRecentEdits()
      if (useEditSession.getState().document) {
        useEditSession.getState().clearSource()
        onRestored?.()
      } else {
        setRecords([])
      }
    } catch (error) {
      surfaceError(error, 'Could not clear recent edits.', { stage: ReportingStage.Persistence })
    } finally {
      setConfirmClear(false)
    }
  }

  if (hideWhenEmpty && !loading && records.length === 0) return null

  return (
    <section data-testid={TEST_IDS.recentEdits} className="w-full space-y-4" aria-labelledby="recent-edits-heading">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 id="recent-edits-heading" className="font-semibold">Recent edits</h2>
          <p className="mt-1 text-xs text-muted-foreground">Stored only in this browser</p>
        </div>
        {records.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" data-testid={TEST_IDS.recentEditsClear} onClick={() => setConfirmClear(true)}>
            Clear all
          </Button>
        ) : null}
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading recent edits…</p> : null}
      {!loading && records.length === 0 ? <p className="rounded-xl border p-5 text-sm text-muted-foreground">No recent edits yet.</p> : null}
      {records.length > 0 ? (
        <div className="grid grid-cols-3 gap-3 lg:grid-cols-5">
          {records.map((record) => {
            const fileName = recordFileName(record)
            return (
              <article key={record.id} data-testid={TEST_IDS.recentEditCard(record.id)} className="group relative min-w-0">
                <button
                  type="button"
                  className="block w-full overflow-hidden rounded-xl border bg-card text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Restore ${fileName}`}
                  onClick={() => requestRestore(record)}
                >
                  <span className="block aspect-square overflow-hidden bg-muted"><Thumbnail record={record} /></span>
                  <span className="block space-y-0.5 p-2">
                    <span className="block truncate text-xs font-medium">{fileName}</span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {summarizeDocument(record.document)} · {formatRelativeDate(record.updatedAt)}
                    </span>
                  </span>
                </button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  data-testid={TEST_IDS.recentEditDelete(record.id)}
                  className="absolute right-1.5 top-1.5 size-7 opacity-90"
                  aria-label={`Delete ${fileName}`}
                  onClick={() => setPendingDelete(record)}
                >
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </Button>
              </article>
            )
          })}
        </div>
      ) : null}

      <Dialog open={pendingRestore !== null} onOpenChange={(open) => { if (!open) setPendingRestore(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch image?</DialogTitle>
            <DialogDescription>Your current edit is already saved in Recent edits.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
            <Button type="button" onClick={() => { if (pendingRestore) void restore(pendingRestore) }}>Switch image</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDelete !== null} onOpenChange={(open) => { if (!open) setPendingDelete(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this recent edit?</DialogTitle>
            <DialogDescription>This removes its stored photo and settings from this browser.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
            <Button type="button" variant="destructive" onClick={() => { if (pendingDelete) void remove(pendingDelete) }}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear all recent edits?</DialogTitle>
            <DialogDescription>This removes every stored photo and closes the current edit.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
            <Button type="button" variant="destructive" onClick={() => void clearAll()}>Clear all</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
